// src/components/layouts/AuthorHeader.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, Search, HelpCircle, Menu, X, User, LogOut, Shield, Award, ChevronDown } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const AuthorHeader = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get user initials
  const getUserInitials = () => {
    if (!user) return 'AU';
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`;
    }
    if (user?.fullname) {
      const names = user.fullname.split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[1][0]}`;
      }
      return names[0][0];
    }
    return 'AU';
  };

  // Get user full name
  const getUserFullName = () => {
    if (!user) return 'Author User';
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user?.fullname) {
      return user.fullname;
    }
    return 'Author User';
  };

  // Get user ORCID
  const getUserOrcid = () => {
    if (!user) return 'Not provided';
    return user?.orcidId || user?.orcid || 'Not provided';
  };

  // Get user affiliation - FIXED: Check if property exists
  const getUserAffiliation = () => {
    if (!user) return '';
    return user?.affiliations || user?.affiliation || '';
  };

  const handleLogout = async () => {
    await logout();
    navigate('/portal/login');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleDashboardSwitch = (path) => {
    navigate(path);
    setShowUserDropdown(false);
    closeMobileMenu();
  };

  return (
    <header className="bg-[--asfi-blue] text-white shadow-md sticky top-0 z-50">
      <div className="container mx-auto px-4 py-3">
        <div className="flex justify-between items-center">
          {/* Logo - visible on mobile */}
          <div className="md:hidden">
            <Link to="/dashboard" className="flex items-center">
              <img 
                src="https://asfirj.org/assets/images/logoIcon/logo.png" 
                alt="site-logo" 
                className="h-8 filter grayscale brightness-0 invert"
              />
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center justify-between w-full">
            {/* Left Navigation */}
            <ul className="flex space-x-6">
              <li>
                <Link 
                  to="/dashboard" 
                  className="hover:text-purple-200 transition flex items-center"
                >
                  <i className="las la-home mr-1"></i>Home
                </Link>
              </li>
              <li>
                <Link 
                  to="/dashboard" 
                  className="hover:text-purple-200 transition flex items-center"
                >
                  <i className="las la-pen mr-1"></i> Author
                </Link>
              </li>
              {user?.canAccessReviewer && (
                <li>
                  <Link 
                    to="/reviewerdash" 
                    className="hover:text-purple-200 transition flex items-center"
                  >
                    <i className="las la-bell mr-1"></i> Review
                  </Link>
                </li>
              )}
              {user?.canAccessEditor && (
                <li>
                  <Link 
                    to="/editors/dashboard" 
                    className="hover:text-purple-200 transition flex items-center"
                  >
                    <i className="las la-edit mr-1"></i> Editorial Assignments
                  </Link>
                </li>
              )}
              <li>
                <Link 
                  to="/messages/inbox" 
                  className="hover:text-purple-200 transition flex items-center"
                >
                  <i className="fa fa-envelope mr-1"></i> Inbox
                </Link>
              </li>
              <li>
                <Link 
                  to="/settings" 
                  className="hover:text-purple-200 transition flex items-center"
                >
                  <i className="fa fa-user mr-1"></i> Settings
                </Link>
              </li>
            </ul>

            {/* User Info with Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowUserDropdown(!showUserDropdown)}
                className="flex items-center space-x-4 ml-4 pl-4 hover:bg-purple-700/30 rounded-lg py-2 px-3 transition-colors"
              >
                <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-bold">{getUserInitials()}</span>
                </div>
                <div className="flex items-center">
                  <div className="text-left">
                    <span className="font-medium text-sm block">{getUserFullName()}</span>
                    <span className="text-xs text-purple-200">{user?.email || ''}</span>
                  </div>
                  <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-200 ${
                    showUserDropdown ? 'transform rotate-180' : ''
                  }`} />
                </div>
              </button>

              {/* User Dropdown Menu */}
              {showUserDropdown && (
                <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                  {/* User Info Header */}
                  <div className="px-4 py-3 border-b border-gray-200">
                    <p className="text-sm font-semibold text-gray-900">{getUserFullName()}</p>
                    <p className="text-xs text-gray-600 mt-1">{user?.email}</p>
                    {user?.orcidId && (
                      <p className="text-xs text-gray-500 mt-1 flex items-center">
                        <Award className="w-3 h-3 mr-1 text-purple-600" />
                        ORCID: {user.orcidId}
                      </p>
                    )}
                    {/* FIXED: Only render if affiliation exists */}
                    {getUserAffiliation() && (
                      <p className="text-xs text-gray-500 mt-1 truncate">
                        {getUserAffiliation()}
                      </p>
                    )}
                    {user?.asfiMembershipId && (
                      <p className="text-xs text-purple-600 mt-1">
                        Member ID: {user.asfiMembershipId}
                      </p>
                    )}
                  </div>

                  {/* Menu Items */}
                  <div className="py-1">
                    <Link
                      to="/settings"
                      onClick={() => setShowUserDropdown(false)}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <User className="w-4 h-4 inline mr-2" />
                      Profile Settings
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setShowUserDropdown(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="w-4 h-4 inline mr-2" />
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="focus:outline-none text-white"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile Navigation Menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu fixed inset-0 z-40 md:hidden overflow-y-auto" style={{ backgroundColor: '#250242' }}>
          <div className="flex justify-between items-center p-4 border-b border-purple-500">
            <Link to="/dashboard" className="flex items-center" onClick={closeMobileMenu}>
              <img 
                src="https://asfirj.org/assets/images/logoIcon/logo.png" 
                alt="site-logo" 
                className="h-10 filter grayscale brightness-0 invert"
              />
              <span className="ml-2 font-bold text-lg text-white">ASFIRJ</span>
            </Link>
            <button 
              onClick={closeMobileMenu}
              className="text-white text-2xl"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <div className="p-4">
            {/* User Info Mobile - Enhanced */}
            <div className="mb-6 p-3 bg-purple-800 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">{getUserInitials()}</span>
                </div>
                <div className="flex-1">
                  <div className="font-bold text-white">{getUserFullName()}</div>
                  <div className="text-sm text-purple-300 break-all">{user?.email || ''}</div>
                </div>
              </div>
              {user?.orcidId && (
                <div className="mt-2 text-xs text-purple-200 flex items-center">
                  <Award className="w-3 h-3 mr-1" />
                  ORCID: {user.orcidId}
                </div>
              )}
              {/* FIXED: Only render if affiliation exists */}
              {getUserAffiliation() && (
                <div className="mt-1 text-xs text-purple-200 truncate">
                  {getUserAffiliation()}
                </div>
              )}
            </div>
            
            {/* Mobile Navigation Links */}
            <nav className="space-y-2">
              <Link 
                to="/dashboard" 
                className="block py-3 px-4 rounded-lg hover:bg-purple-800 text-white transition"
                onClick={closeMobileMenu}
              >
                <i className="las la-home mr-3"></i> Home
              </Link>
              <Link 
                to="/dashboard" 
                className="block py-3 px-4 rounded-lg hover:bg-purple-800 text-white transition"
                onClick={closeMobileMenu}
              >
                <i className="las la-pen mr-3"></i> Author
              </Link>
              {user?.canAccessReviewer && (
                <Link 
                  to="/reviewerdash" 
                  className="block py-3 px-4 rounded-lg hover:bg-purple-800 text-white transition"
                  onClick={closeMobileMenu}
                >
                  <i className="las la-bell mr-3"></i> Review
                </Link>
              )}
              {user?.canAccessEditor && (
                <Link 
                  to="/editors/dashboard" 
                  className="block py-3 px-4 rounded-lg hover:bg-purple-800 text-white transition"
                  onClick={closeMobileMenu}
                >
                  <i className="las la-edit mr-3"></i> Editorial Assignments
                </Link>
              )}
              <Link 
                to="/messages/inbox" 
                className="block py-3 px-4 rounded-lg hover:bg-purple-800 text-white transition"
                onClick={closeMobileMenu}
              >
                <i className="fa fa-envelope mr-3"></i> Inbox
              </Link>
              <Link 
                to="/settings" 
                className="block py-3 px-4 rounded-lg hover:bg-purple-800 text-white transition"
                onClick={closeMobileMenu}
              >
                <i className="fa fa-user mr-3"></i> Settings
              </Link>
              <button
                onClick={() => {
                  handleLogout();
                  closeMobileMenu();
                }}
                className="w-full text-left block py-3 px-4 rounded-lg hover:bg-red-700 text-white transition mt-4"
              >
                <i className="fas fa-sign-out-alt mr-3"></i> Logout
              </button>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default AuthorHeader;